import React, {useEffect, useMemo, useRef, useState} from 'react';
import './App.css';

function App() {
    // === CONFIG ===
    const API_BASE = 'https://sehdini-loto.moolsbytheway.workers.dev'; // http://localhost:8787';
    //const API_BASE = 'http://localhost:8787';

    // JWT token management
    const [authToken, setAuthToken] = useState(() => {
        // Load token from localStorage on mount
        return localStorage.getItem('jwt_token') || null;
    });

    // UI state
    const [step, setStep] = useState('START');      // START | INTRO | PROGRESS | SUCCESS | LIMIT
    const [adFinished, setAdFinished] = useState(false);
    const [progress, setProgress] = useState(0);
    const [phone, setPhone] = useState('');
    const [attempts, setAttempts] = useState(0);
    const [maxAttempts, setMaxAttempts] = useState(3);
    const [isLoading, setIsLoading] = useState(false); // Loading state for buttons

    // Ads + current video ref
    const [ads, setAds] = useState([]);
    const videoRef = useRef(null);

    // Handle token updates - save to localStorage
    useEffect(() => {
        if (authToken) {
            localStorage.setItem('jwt_token', authToken);
        } else {
            localStorage.removeItem('jwt_token');
        }
    }, [authToken]);

    // Tickets for secure flow
    const [introTicket, setIntroTicket] = useState(null);
    const [progressTicket, setProgressTicket] = useState(null);

    // Media loading state
    const [mediaLoaded, setMediaLoaded] = useState(false);

    // Helper to make authenticated requests
    const authFetch = async (url, options = {}) => {
        const headers = {
            ...options.headers,
        };

        if (authToken) {
            headers['Authorization'] = `Bearer ${authToken}`;
        }

        const response = await fetch(url, {
            ...options,
            headers,
        });

        // Update token if server returns a new one
        if (response.ok) {
            try {
                const data = await response.clone().json();
                if (data.token) {
                    setAuthToken(data.token);
                }
            } catch (e) {
                // Response might not be JSON
            }
        }

        return response;
    };

    // === 1) Load attempts from server ===
    useEffect(() => {
        (async () => {
            try {
                const r = await authFetch(`${API_BASE}/session`);
                if (!r.ok) throw new Error('session failed');
                const data = await r.json();
                setAttempts(data.attempts || 0);
                setMaxAttempts(data.max || 3);
                if ((data.attempts || 0) >= (data.max || 3)) setStep('LIMIT');
            } catch {
                // fallback: keep defaults
            }
        })();
    }, []);

    // === 2) Load ads from backend ===
    useEffect(() => {
        let mounted = true;
        authFetch(`${API_BASE}/ads.json`)
            .then(r => r.json())
            .then((data) => {
                if (mounted) setAds(Array.isArray(data) ? data : []);
            })
            .catch(() => {
                if (mounted) {
                    setAds([]);
                }
            });
        return () => {
            mounted = false;
        };
    }, []);

    // Pick ad per step
    const currentAd = useMemo(() => {
        if (step === 'INTRO') return ads[0] || null;
        if (step === 'PROGRESS') return ads[1] || null;
        return null;
    }, [ads, step]);

    // === 3) Secure flow: get the INTRO ticket when entering INTRO ===
    useEffect(() => {
        if (step !== 'INTRO') return;
        setAdFinished(false);
        setProgress(0);
        setIntroTicket(null);
        setMediaLoaded(false);
        (async () => {
            try {
                const r = await authFetch(`${API_BASE}/ad-ticket?slot=intro`);
                const data = await r.json();
                setIntroTicket(data.ticketId || null);
            } catch {
                setIntroTicket(null);
            }
        })();
    }, [step]);

    // === 4) Manage progress for IMAGE ads (10s) on both INTRO and PROGRESS ===
    useEffect(() => {
        if (!currentAd || !mediaLoaded) return;
        setProgress(0);
        if (step === 'INTRO') setAdFinished(false);

        if (currentAd.type === 'image') {
            const total = 10000; // 10 seconds for photos
            const start = Date.now();
            const timer = setInterval(async () => {
                const elapsed = Date.now() - start;
                const pct = Math.min(100, (elapsed / total) * 100);
                setProgress(pct);
                if (pct >= 100) {
                    clearInterval(timer);
                    if (step === 'INTRO') {
                        // prove intro ad completion
                        await completeAd('intro', introTicket);
                        setAdFinished(true);
                    } else if (step === 'PROGRESS') {
                        await onProgressAdFinished();
                    }
                }
            }, 100);
            return () => clearInterval(timer);
        }
        // For videos, progress comes from onTimeUpdate handlers.
    }, [currentAd, mediaLoaded, step, introTicket, progressTicket]);

    // === Helpers to call backend ===
    const completeAd = async (slot, ticketId) => {
        if (!ticketId) return;
        try {
            const body = {slot, ticketId};
            const r = await authFetch(`${API_BASE}/ad-complete`, {
                method: 'POST',
                headers: {'Content-Type': 'application/json'},
                body: JSON.stringify(body),
            });
            if (!r.ok) {
                // optional: show toast
            }
        } catch (e) {
            // optional: show toast
        }
    };

    const startSubscription = async () => {
        setIsLoading(true);
        try {
            const r = await authFetch(`${API_BASE}/subscribe/start`, {
                method: 'POST',
                headers: {'Content-Type': 'application/json'},
                body: JSON.stringify({phone}),
            });
            if (!r.ok) {
                if (r.status === 429) setStep('LIMIT');
                return;
            }
            const data = await r.json();
            setProgressTicket(data.progressTicket || null);
            setStep('PROGRESS');
        } catch {
            // optional: toast
        } finally {
            setIsLoading(false);
        }
    };

    const finishSubscription = async () => {
        try {
            const r = await authFetch(`${API_BASE}/subscribe/finish`, {
                method: 'POST',
                headers: {'Content-Type': 'application/json'},
                body: JSON.stringify({ticketId: progressTicket}),
            });
            if (!r.ok) {
                if (r.status === 429) {
                    setStep('LIMIT');
                    return;
                }
                // optional: toast error
                return;
            }
            const data = await r.json();
            setAttempts(data.attempts ?? attempts + 1);
            setStep('SUCCESS');
        } catch {
            // optional: toast
        }
    };

    // === PROGRESS helpers ===
    const onProgressAdFinished = async () => {
        setProgress(100);
        if (!progressTicket) return;
        await completeAd('progress', progressTicket);
        await finishSubscription();
    };

    const handleSignInClick = async () => {
        if (!phone) return;
        await startSubscription();
    };

    // Video time updates drive the progress bar
    const handleVideoTimeUpdate = (e) => {
        const v = e.target;
        if (v && v.duration) {
            setProgress((v.currentTime / v.duration) * 100);
        }
    };

    const handleVideoEnded = async () => {
        setProgress(100);
        if (step === 'INTRO') {
            await completeAd('intro', introTicket);
            setAdFinished(true);
        } else if (step === 'PROGRESS') {
            await onProgressAdFinished();
        }
    };

    // Handle media load events
    const handleMediaLoaded = () => {
        setMediaLoaded(true);
    };

    const handleVideoCanPlay = (e) => {
        setMediaLoaded(true);
        // Start playing if not already
        const v = e.target;
        if (v.paused) {
            v.play().catch(() => {
            });
        }
    };

    const tryAgain = () => {
        if (attempts >= maxAttempts) {
            setStep('LIMIT');
        } else {
            setPhone('');
            setAdFinished(false);
            setProgress(0);
            setIntroTicket(null);
            setProgressTicket(null);
            setMediaLoaded(false);
            setStep('INTRO');
        }
    };

    /** START */
    const renderStart = () => (
        <div className="page">
            <div className="section header">
                <div className="logo"></div>
                <div className="title" style={{textAlign: 'center'}}>
                    Welcome to Sehdini Daily Cash<br/>
                    Press Start to begin
                </div>
            </div>

            <div className="section footer">
                <button
                    className="button"
                    onClick={() => setStep('INTRO')}
                >
                    Start
                </button>
            </div>
            <div className="ad"/>
        </div>
    );

    /** INTRO */
    const renderIntro = () => (
        <div className="page">
            <div className="section header">
                <div className="title">
                    You can sign in for the daily withdrawal after the ad finishes
                </div>
                {currentAd && !adFinished && (
                    <div className="progress-container">
                        <div className="progress-bar"
                             style={{
                                 width: `${progress}%`,
                                 transition: 'width 0.3s ease-out'
                             }}/>
                    </div>
                )}
            </div>

            {currentAd && !adFinished && (
                <div className="ad">
                    {currentAd.type === 'video' ? (
                        <video
                            key={currentAd.src}
                            ref={videoRef}
                            src={currentAd.src}
                            autoPlay
                            preload="auto"
                            onTimeUpdate={handleVideoTimeUpdate}
                            onEnded={handleVideoEnded}
                            onCanPlay={handleVideoCanPlay}
                            controls={false}
                        />
                    ) : (
                        <img
                            src={currentAd.src}
                            alt="Advertisement"
                            onLoad={handleMediaLoaded}
                        />
                    )}
                    {!mediaLoaded && (
                        <div style={{
                            position: 'absolute',
                            top: '50%',
                            left: '50%',
                            transform: 'translate(-50%, -50%)',
                            color: 'white',
                            fontSize: '14px'
                        }}>
                            Loading...
                        </div>
                    )}
                </div>
            )}

            {adFinished && (
                <div className="section footer">
                    <input
                        type="tel"
                        className="input"
                        placeholder="Enter your phone number"
                        value={phone}
                        onChange={(e) => setPhone(e.target.value)}
                        inputMode="tel"
                        autoComplete="tel"
                        style={{fontSize: '16px'}}
                    />
                    <button
                        className="button"
                        onClick={handleSignInClick}
                        disabled={!phone || isLoading}
                    >
                        {isLoading && <span className="spinner"></span>}
                        Sign in to daily withdrawal
                    </button>
                </div>
            )}
        </div>
    );

    /** PROGRESS */
    const renderProgress = () => (
        <div className="page">
            <div className="section header">
                <div className="title">
                    Subscription to daily withdrawal in progress<br/>
                    Please don't close the page until the progress finishes
                </div>
                {currentAd && (
                    <div className="progress-container">
                        <div className="progress-bar"
                             style={{
                                 width: `${progress}%`,
                                 transition: 'width 0.3s ease-out'
                             }}/>
                    </div>
                )}
            </div>

            <div className="ad">
                {currentAd ? (
                    currentAd.type === 'video' ? (
                        <>
                            <video
                                key={currentAd.src}
                                ref={videoRef}
                                src={currentAd.src}
                                autoPlay
                                preload="auto"
                                onTimeUpdate={handleVideoTimeUpdate}
                                onEnded={handleVideoEnded}
                                onCanPlay={handleVideoCanPlay}
                                controls={false}
                            />
                            {!mediaLoaded && (
                                <div style={{
                                    position: 'absolute',
                                    top: '50%',
                                    left: '50%',
                                    transform: 'translate(-50%, -50%)',
                                    color: 'white',
                                    fontSize: '14px'
                                }}>
                                    Loading...
                                </div>
                            )}
                        </>
                    ) : (
                        <>
                            <img
                                src={currentAd.src}
                                alt="Progress Advertisement"
                                onLoad={handleMediaLoaded}
                            />
                            {!mediaLoaded && (
                                <div style={{
                                    position: 'absolute',
                                    top: '50%',
                                    left: '50%',
                                    transform: 'translate(-50%, -50%)',
                                    color: 'white',
                                    fontSize: '14px'
                                }}>
                                    Loading...
                                </div>
                            )}
                        </>
                    )
                ) : (
                    <div style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        width: '100%',
                        height: '100%'
                    }}>
                        <p style={{opacity: 0.6}}>No ad configured</p>
                    </div>
                )}
            </div>
        </div>
    );

    const renderSuccess = () => (
        <div className="page">
            <div className="section header">
                <div className="success-animation">🎉</div>
                <div className="title">You are subscribed to daily withdrawal!</div>
                <p style={{fontSize: 'var(--text-size)', textAlign: 'center'}}>
                    Check at 6 PM on Sehdini official page for winner declaration.
                </p>
            </div>

            <div className="ad"/>

            <div className="section footer">
                {attempts < maxAttempts ? (
                    <button className="button" onClick={tryAgain}>
                        Subscribe again
                    </button>
                ) : (
                    <button className="button" onClick={() => setStep('LIMIT')}>
                        Finish
                    </button>
                )}
            </div>
        </div>
    );

    const renderLimit = () => (
        <div className="page">
            <div className="section header">
                <div className="title" style={{textAlign: 'center'}}>
                    You have already subscribed three times today.<br/>
                    Please try again tomorrow.
                </div>
            </div>

            <div className="ad"/>

            <div className="section footer">
                <button className="button" onClick={() => setStep('INTRO')}>
                    Back
                </button>
            </div>
        </div>
    );

    let toBeRendered = <div/>;
    switch (step) {
        case 'START':
            toBeRendered = renderStart();
            break;
        case 'INTRO':
            toBeRendered = renderIntro();
            break;
        case 'PROGRESS':
            toBeRendered = renderProgress();
            break;
        case 'SUCCESS':
            toBeRendered = renderSuccess();
            break;
        case 'LIMIT':
            toBeRendered = renderLimit();
            break;
        default:
            toBeRendered = renderStart();
    }

    return <div className="container">{toBeRendered}</div>;
}

export default App;
